class EncryptionService {
  constructor () {
    this.encryptionKey = null
    this.algorithm = {
      name: 'AES-GCM',
      length: 256
    }
  }

  async initializeKey (jwtToken) {
    if (!jwtToken) {
      throw new Error('JWT token is required for encryption initialization')
    }

    try {
      // Use the JWT token as the base for key derivation
      const encoder = new TextEncoder()
      const keyMaterial = encoder.encode(jwtToken)

      // Import the raw key material
      const importedKey = await window.crypto.subtle.importKey(
        'raw',
        await this.hashData(keyMaterial),
        { name: 'PBKDF2' },
        false,
        ['deriveBits', 'deriveKey']
      )

      // Derive the actual encryption key
      this.encryptionKey = await window.crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt: encoder.encode('LSGLandmeters-Salt'),
          iterations: 100000,
          hash: 'SHA-256'
        },
        importedKey,
        this.algorithm,
        false,
        ['encrypt', 'decrypt']
      )

      return true
    } catch (error) {
      console.error('Failed to initialize encryption key:', error)
      throw error
    }
  }

  async hashData (data) {
    return await window.crypto.subtle.digest('SHA-256', data)
  }

  async encrypt (data) {
    if (!this.encryptionKey) {
      throw new Error('Encryption key not initialized. User must be logged in.')
    }

    try {
      // Convert data to JSON string
      const jsonString = JSON.stringify(data)
      const encoder = new TextEncoder()
      const dataBuffer = encoder.encode(jsonString)

      // Generate a random initialization vector
      const iv = window.crypto.getRandomValues(new Uint8Array(12))

      // Encrypt the data
      const encryptedBuffer = await window.crypto.subtle.encrypt(
        {
          name: this.algorithm.name,
          iv
        },
        this.encryptionKey,
        dataBuffer
      )

      // Combine IV and encrypted data
      const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength)
      combined.set(iv, 0)
      combined.set(new Uint8Array(encryptedBuffer), iv.length)

      // Convert to base64 for storage
      return this.arrayBufferToBase64(combined)
    } catch (error) {
      console.error('Encryption failed:', error)
      throw error
    }
  }

  async decrypt (encryptedData) {
    if (!this.encryptionKey) {
      throw new Error('Encryption key not initialized. User must be logged in.')
    }

    try {
      // Convert from base64
      const combined = this.base64ToArrayBuffer(encryptedData)

      // Extract IV and encrypted data
      const iv = combined.slice(0, 12)
      const encryptedBuffer = combined.slice(12)

      // Decrypt the data
      const decryptedBuffer = await window.crypto.subtle.decrypt(
        {
          name: this.algorithm.name,
          iv
        },
        this.encryptionKey,
        encryptedBuffer
      )

      // Convert back to string and parse JSON
      const decoder = new TextDecoder()
      const jsonString = decoder.decode(decryptedBuffer)
      return JSON.parse(jsonString)
    } catch (error) {
      console.error('Decryption failed:', error)
      throw error
    }
  }

  isAvailable () {
    return this.encryptionKey !== null
  }

  clearKey () {
    this.encryptionKey = null
  }

  arrayBufferToBase64 (buffer) {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    return window.btoa(binary)
  }

  base64ToArrayBuffer (base64) {
    const binary = window.atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }
    return bytes
  }
}

export default new EncryptionService()
